/**
 * Breadcrumb Context Manager for Enzzo
 * Tracks collection context when navigating from collection to product pages.
 * Uses sessionStorage so the breadcrumb shows: Home > Collection > Product
 * even when Liquid doesn't have the collection context available.
 */

class BreadcrumbManager {
  constructor() {
    this.STORAGE_KEY = 'enzzo_breadcrumb_collection';
    this.init();
  }

  init() {
    this.attachNavigationTracking();

    if (this.isCollectionPage()) {
      this.trackCollectionClicks();
    }

    if (this.isProductPage()) {
      this.updateProductBreadcrumb();
    }

    this.cleanupOldEntries();
  }

  isCollectionPage() {
    return window.location.pathname.includes('/collections/');
  }

  isProductPage() {
    return window.location.pathname.includes('/products/');
  }

  /**
   * Track clicks on product links from collection pages
   * so we know which collection the user came from.
   */
  trackCollectionClicks() {
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('a[href*="/products/"]');
      if (!productLink) return;

      const collectionData = this.getCurrentCollectionData();
      if (collectionData) {
        this.saveCollectionContext(productLink.getAttribute('href'), collectionData);
      }
    }, true);
  }

  /**
   * Track collection link clicks from anywhere (navbar, menu drawer, etc.)
   * so when a user clicks a nav link to a collection and then a product,
   * we have the parent collection context.
   */
  attachNavigationTracking() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.breadcrumbs')) return;

      const collectionLink = e.target.closest('a[href*="/collections/"]');
      if (!collectionLink) return;

      const href = collectionLink.getAttribute('href');
      const handle = href.match(/\/collections\/([^\/\?#]+)/)?.[1];
      if (!handle) return;

      // Check if this is inside a submenu (child collection)
      const submenu = collectionLink.closest('.menu-drawer__submenu') ||
                      collectionLink.closest('.links_with_submenu');

      if (submenu) {
        const details = submenu.closest('details');
        if (details) {
          const summary = details.querySelector(':scope > summary');
          if (summary) {
            const parentAnchor = summary.querySelector('a[href]');
            let parentUrl = '';
            let parentTitle = '';

            if (parentAnchor) {
              parentUrl = parentAnchor.getAttribute('href');
              parentTitle = parentAnchor.textContent.trim();
            } else {
              parentTitle = summary.textContent.trim();
            }

            if (parentTitle) {
              this.saveParentContext(handle, {
                url: parentUrl,
                title: parentTitle
              });
            }
          }
        }
      }
    }, true);
  }

  // --- Parent context (for nested collections in nav) ---

  saveParentContext(childHandle, parentData) {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY + '_parents');
      const allData = stored ? JSON.parse(stored) : {};
      allData[childHandle] = {
        url: parentData.url,
        title: parentData.title,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.STORAGE_KEY + '_parents', JSON.stringify(allData));
    } catch (e) { /* silent */ }
  }

  getParentContext(collectionHandle) {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY + '_parents');
      if (!stored) return null;
      const allData = JSON.parse(stored);
      return allData[collectionHandle] || null;
    } catch (e) {
      return null;
    }
  }

  // --- Collection → Product context ---

  getCurrentCollectionData() {
    const currentUrl = window.location.pathname;
    const match = currentUrl.match(/\/collections\/([^\/]+)/);
    if (!match) return null;

    const handle = match[1];
    let title = document.querySelector('h1')?.textContent?.trim()
             || document.title.split('–')[0]?.trim()
             || this.formatHandle(handle);

    title = this.cleanTitle(title);

    return {
      handle: handle,
      title: title,
      url: '/collections/' + handle,
      timestamp: Date.now()
    };
  }

  saveCollectionContext(productUrl, collectionData) {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      const allData = stored ? JSON.parse(stored) : {};
      const productHandle = productUrl.match(/\/products\/([^\/\?#]+)/)?.[1];
      if (!productHandle) return;

      allData[productHandle] = collectionData;
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(allData));
    } catch (e) { /* silent */ }
  }

  getCollectionContext(productHandle) {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      const allData = JSON.parse(stored);
      return allData[productHandle] || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * On a product page, inject the collection breadcrumb if Liquid didn't render one
   * (i.e., the user arrived via direct link, search, etc. but we have sessionStorage context).
   */
  updateProductBreadcrumb() {
    const productHandle = window.location.pathname.match(/\/products\/([^\/\?#]+)/)?.[1];
    if (!productHandle) return;

    const breadcrumbList = document.getElementById('breadcrumb-list');
    if (!breadcrumbList) return;

    // If Liquid already rendered a collection breadcrumb, we're done
    const existingCollection = breadcrumbList.querySelector('.breadcrumb--collection');
    if (existingCollection) return;

    // Try to get collection context from sessionStorage
    const collectionData = this.getCollectionContext(productHandle);
    if (!collectionData) return;

    const productItem = breadcrumbList.querySelector('.breadcrumb--product');
    if (!productItem) return;

    // Insert collection breadcrumb before the product
    const li = document.createElement('li');
    li.className = 'pdg-0 mrg-r10 breadcrumb--collection';
    li.innerHTML = '<a class="txt-14 pfDin" href="' + this.escapeHtml(collectionData.url) + '">'
                 + this.escapeHtml(collectionData.title) + '</a>';
    productItem.before(li);
  }

  // --- Utilities ---

  cleanupOldEntries() {
    try {
      [this.STORAGE_KEY, this.STORAGE_KEY + '_parents'].forEach(key => {
        const stored = sessionStorage.getItem(key);
        if (!stored) return;
        const allData = JSON.parse(stored);
        const oneHourAgo = Date.now() - 3600000;
        let changed = false;
        Object.keys(allData).forEach(k => {
          if (allData[k].timestamp < oneHourAgo) {
            delete allData[k];
            changed = true;
          }
        });
        if (changed) sessionStorage.setItem(key, JSON.stringify(allData));
      });
    } catch (e) { /* silent */ }
  }

  formatHandle(handle) {
    return handle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  cleanTitle(title) {
    return title
      .replace(/^Collection:\s*/i, '')
      .replace(/^Collections:\s*/i, '')
      .replace(/^Συλλογή:\s*/i, '')
      .replace(/^Συλλογές:\s*/i, '')
      .trim();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.breadcrumbManager = new BreadcrumbManager();
  });
} else {
  window.breadcrumbManager = new BreadcrumbManager();
}
